/**
 * 公司详情页面 — CompanyDetail（按概念稿 company-detail.html 重排）。
 *
 * 信息架构（对齐概念稿 + 验收清单"4 指标卡 + 趋势区 + 预警卡"）：
 *   1. 指标卡 ×4：品牌数 / 产品数 / 本月诊断 / 本月投放费用
 *   2. 趋势区（AI 可见性 · 近期趋势）+ 预警卡（关联预警 · 名下品牌）
 *   3. g2：左 基本信息卡（原"主体身份/联系信息/业务与内容"三卡合并为一张 kv 卡，减少滚动）
 *          右 账户动态卡（dashboard recent_activities）
 *   4. 名下品牌列表卡（DataTable，行点击进 /brands/:id）
 *
 * 数据口径说明（不虚构接口）：
 *   - 产品数/本月诊断/本月投放费用来自 GET /api/dashboard?company_id= 的 stats；
 *     后端支持 company_id 过滤即公司维度口径，未过滤时为账户级口径（以实际返回为准）
 *   - 品牌数/名下品牌来自 GET /api/brands?company_id=；每品牌产品数取
 *     GET /api/products?brand_id=&size=1 的 total（独立失败域，失败显 "—"）
 *   - 趋势区：GET /api/ai-visibility?company_id=（跨品牌汇总，公司维度），
 *     前端按 recorded_at 日期聚合提及率均值成折线；接口无公司级独立趋势端点，
 *     按"已有接口数据前端聚合"实现，不虚构字段
 *   - 预警卡：GET /api/alerts 无 company 维度参数，取租户列表后按名下品牌
 *     brand_id 前端过滤（与 ProductDetail 客户端过滤同模式），未处理优先、P0 在前
 *   - 账户动态同 dashboard 口径；各接口独立失败域，失败仅对应区块降级，不红屏
 *
 * 保留：批 1 EmptyState 兜底（无 id 引导）、面包屑、只读呈现（PRD 1.2）。
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BusinessOutlined,
  Inventory2Outlined,
  FactCheckOutlined,
  PaymentsOutlined,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import StatCard from "../components/StatCard";
import Badge from "../components/Badge";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import AlertItem from "../components/AlertItem";
import { getCompany } from "../api/companies";
import { getBrands } from "../api/brands";
import { getProducts } from "../api/products";
import { getDashboard } from "../api/dashboard";
import { getAiVisibility } from "../api/visibility";
import { getAlerts } from "../api/alerts";
import { useCompanyStore } from "../store/companyStore";
import { formatCurrency, formatRelativeTime } from "../utils/format";
import type {
  Company,
  Brand,
  DashboardData,
  AiVisibility,
  AlertItem as AlertItemData,
} from "../types";

/** 字段行：标签 + 值。 */
function FieldRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: value ? "text.primary" : "text.disabled",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Grid>
  );
}

/** 信息卡内的小节标题（原三分组的弱分隔）。 */
function SectionLabel({ text }: { text: string }) {
  return (
    <Typography
      sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 1, mt: 2 }}
    >
      {text}
    </Typography>
  );
}

export default function CompanyDetail() {
  const muiTheme = useTheme();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const effectiveCompanyId = companyId || selectedCompanyId;
  const [data, setData] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 概念稿新增区块：品牌列表 / 每品牌产品数 / dashboard 聚合（均为独立失败域）
  const [brands, setBrands] = useState<Brand[] | null>(null);
  const [brandProductCounts, setBrandProductCounts] = useState<
    Record<string, number | null>
  >({});
  const [dash, setDash] = useState<DashboardData | null>(null);
  const [dashFailed, setDashFailed] = useState(false);

  // 趋势区 + 预警卡（验收清单要求；独立失败域，降级不红屏）
  const [visItems, setVisItems] = useState<AiVisibility[] | null>(null);
  const [visFailed, setVisFailed] = useState(false);
  const [alerts, setAlerts] = useState<AlertItemData[] | null>(null);
  const [alertsFailed, setAlertsFailed] = useState(false);

  useEffect(() => {
    if (effectiveCompanyId) {
      setLoading(true);
      setError("");
      getCompany(effectiveCompanyId)
        .then(setData)
        .catch((err) =>
          setError(
            err instanceof Error ? err.message : "获取公司详情失败",
          ),
        )
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [effectiveCompanyId]);

  /** 名下品牌 + 每品牌产品数（products 列表接口 total 口径）。 */
  useEffect(() => {
    if (!effectiveCompanyId) {
      setBrands(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await getBrands(effectiveCompanyId);
        if (cancelled) return;
        setBrands(list);
        // 每品牌产品数：size=1 仅取 total；单品牌失败不影响其他（显 "—"）
        const entries = await Promise.all(
          list.map(async (b) => {
            try {
              const res = await getProducts({ brand_id: b.id, size: 1 });
              return [b.id, res.total] as const;
            } catch {
              return [b.id, null] as const;
            }
          }),
        );
        if (!cancelled) setBrandProductCounts(Object.fromEntries(entries));
      } catch {
        if (!cancelled) setBrands([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId]);

  /** dashboard 聚合（指标卡 + 账户动态；失败仅降级，不阻断页面）。 */
  useEffect(() => {
    if (!effectiveCompanyId) {
      setDash(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await getDashboard(effectiveCompanyId);
        if (!cancelled) {
          setDash(d);
          setDashFailed(false);
        }
      } catch {
        if (!cancelled) {
          setDash(null);
          setDashFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId]);

  /** 趋势区数据源：GET /api/ai-visibility?company_id=（跨品牌汇总，公司维度）。 */
  useEffect(() => {
    if (!effectiveCompanyId) {
      setVisItems(null);
      return;
    }
    let cancelled = false;
    getAiVisibility(effectiveCompanyId)
      .then((list) => {
        if (!cancelled) {
          setVisItems(list);
          setVisFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVisItems(null);
          setVisFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId]);

  /**
   * 预警卡数据源：alerts 接口无 company 维度参数，
   * 取租户列表后按名下品牌 brand_id 前端过滤（ProductDetail 同模式）。
   */
  useEffect(() => {
    if (!effectiveCompanyId || brands === null) return;
    const brandIds = new Set(brands.map((b) => b.id));
    let cancelled = false;
    getAlerts({ size: 100 })
      .then((res) => {
        if (cancelled) return;
        const open = (res.items || [])
          .filter(
            (a) =>
              a.brand_id && brandIds.has(a.brand_id) && a.status !== "resolved",
          )
          .sort((a, b) => {
            if (a.level !== b.level) return a.level === "P0" ? -1 : 1;
            return (b.last_triggered_at || "").localeCompare(
              a.last_triggered_at || "",
            );
          });
        setAlerts(open);
        setAlertsFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAlerts(null);
          setAlertsFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId, brands]);

  /** 标记处理成功：本地置 resolved（与 ProductDetail 同模式）。 */
  const handleAlertResolved = (id: string) => {
    setAlerts((prev) =>
      prev
        ? prev.map((a) =>
            a.id === id ? { ...a, status: "resolved" as const } : a,
          )
        : prev,
    );
  };

  /**
   * 趋势折线数据：按 recorded_at 日期聚合提及率均值（公司名下全部品牌×平台）。
   * 接口无公司级独立趋势端点，此为已有接口数据的前端聚合，不虚构字段。
   */
  const trendData = useMemo(() => {
    if (!visItems || visItems.length === 0) return [];
    const byDate = new Map<string, { sum: number; n: number }>();
    for (const v of visItems) {
      const d = (v.recorded_at || "").slice(0, 10);
      if (!d) continue;
      const cur = byDate.get(d) || { sum: 0, n: 0 };
      cur.sum += v.mention_rate;
      cur.n += 1;
      byDate.set(d, cur);
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { sum, n }]) => ({
        date: date.slice(5, 10),
        score: +(sum / n).toFixed(1),
        samples: n,
      }));
  }, [visItems]);

  if (!effectiveCompanyId) {
    return (
      <Box>
        <PageHeader
          title="公司"
          breadcrumbs={[{ label: "资产管理" }, { label: "公司" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先选择一个公司"
              subtitle="使用页面顶部的公司选择器，选择要查看的公司"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="公司详情" />
        <Loading fullPage text="正在加载公司详情..." />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <PageHeader title="公司详情" />
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>
              {error || "公司不存在"}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const stats = dash?.stats;
  const activities = dash?.recent_activities ?? [];
  const p0Count = alerts?.filter((a) => a.level === "P0").length ?? 0;
  const p1Count = alerts?.filter((a) => a.level === "P1").length ?? 0;

  const brandColumns: DataTableColumn<Brand>[] = [
    {
      key: "name",
      label: "品牌名",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
            {row.name}
          </Typography>
          {row.brand_positioning && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {row.brand_positioning}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "full_name",
      label: "品牌全称",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.full_name || "—"}
        </Typography>
      ),
    },
    {
      key: "product_count",
      label: "产品数",
      align: "right",
      render: (row) => {
        const count = brandProductCounts[row.id];
        return (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
            {count === null || count === undefined ? "—" : count}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={data.name}
        subtitle={data.industry ? `客户公司 · ${data.industry}` : "客户公司"}
        breadcrumbs={[
          { label: "资产管理" },
          { label: "公司", path: "/companies" },
          { label: data.name },
        ]}
      />

      {/* ===== 指标卡 ×4（概念稿：品牌数/产品数/本月诊断/本月投放费用） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="品牌数"
            value={brands ? brands.length : "—"}
            subtitle={
              brands && brands.length > 0
                ? brands.map((b) => b.name).join(" · ")
                : "名下暂无品牌"
            }
            icon={<BusinessOutlined />}
            iconBg="brand.50"
            iconColor="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="产品数"
            value={stats ? stats.product_count : "—"}
            subtitle={stats ? undefined : "看板接口未接入"}
            icon={<Inventory2Outlined />}
            iconBg="semanticBg.info"
            iconColor="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="本月诊断"
            value={stats ? stats.monthly_diagnoses : "—"}
            subtitle={stats ? "GHS 诊断次数" : "看板接口未接入"}
            icon={<FactCheckOutlined />}
            iconBg="semanticBg.success"
            iconColor="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="本月投放费用"
            value={stats ? formatCurrency(stats.monthly_delivery_confirmed) : "—"}
            subtitle={
              stats
                ? `待确认 ${formatCurrency(stats.monthly_delivery_pending)}`
                : "看板接口未接入"
            }
            icon={<PaymentsOutlined />}
            iconBg="semanticBg.warning"
            iconColor="warning.main"
          />
        </Grid>
      </Grid>

      {/* ===== 趋势区 + 预警卡（验收清单：公司详情 = 4 指标卡 + 趋势区 + 预警卡） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 左：AI 可见性近期趋势（/api/ai-visibility?company_id= 前端按日聚合） */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">AI 可见性 · 近期趋势</Typography>
                {visItems && visItems.length > 0 && (
                  <Badge
                    label={`${new Set(visItems.map((v) => v.brand_id)).size} 品牌 · ${new Set(visItems.map((v) => v.platform)).size} 平台`}
                    variant="info"
                  />
                )}
              </Box>
              {visItems === null && !visFailed ? (
                <Loading text="正在加载趋势..." />
              ) : visFailed ? (
                <EmptyState
                  text="趋势数据暂未接入"
                  source="由洞悉引擎周期探测产出，接口接入后自动展示"
                />
              ) : trendData.length === 0 ? (
                <EmptyState
                  text="暂无可见性探测数据"
                  source="由洞悉引擎周期探测产出 · 产出后自动展示"
                />
              ) : (
                <>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                    名下品牌 × 平台提及率 · 按日平均
                  </Typography>
                  <Box sx={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={trendData}
                        margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={muiTheme.palette.lineSoft}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="date"
                          tick={{
                            fontSize: 11,
                            fill: muiTheme.palette.text.disabled,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tick={{
                            fontSize: 11,
                            fill: muiTheme.palette.text.disabled,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <ChartTooltip
                          formatter={(value, _name, item) => [
                            `${value ?? "—"} 分（n=${(item?.payload as { samples?: number })?.samples ?? "—"}）`,
                            "提及率均值",
                          ]}
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke={muiTheme.palette.primary.main}
                          strokeWidth={2}
                          dot={{ r: 2.5 }}
                          activeDot={{ r: 4 }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 右：关联预警（按名下品牌前端过滤，AlertItem 复用） */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">当前预警 · 名下品牌</Typography>
                {alerts && p0Count > 0 && (
                  <Badge label={`P0 ×${p0Count}`} variant="error" />
                )}
                {alerts && p1Count > 0 && (
                  <Badge label={`P1 ×${p1Count}`} variant="warning" />
                )}
              </Box>
              {alerts === null && !alertsFailed ? (
                <Loading text="正在加载预警..." />
              ) : alertsFailed ? (
                <EmptyState
                  text="预警流暂未接入"
                  source="由预警引擎按规则周期巡检产出，联调完成后自动展示"
                />
              ) : alerts && alerts.length === 0 ? (
                <EmptyState
                  text="暂无名下品牌相关未处理预警"
                  source="由预警引擎按规则周期巡检产出"
                  hint="P0/P1 规则触发后自动出现在这里"
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(alerts || []).slice(0, 5).map((a) => (
                    <AlertItem
                      key={a.id}
                      item={a}
                      onResolved={handleAlertResolved}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== 基本信息 + 账户动态（概念稿 g2） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 左：基本信息（原三分组合并为一张 kv 卡，字段不变） */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h4">基本信息</Typography>

              <SectionLabel text="主体身份" />
              <Grid container spacing={2}>
                <FieldRow label="公司名称" value={data.name} />
                <FieldRow label="法人名称" value={data.legal_name} />
                <FieldRow label="成立日期" value={data.founded} />
                <FieldRow label="所属行业" value={data.industry} />
                <FieldRow label="公司 Logo" value={data.logo} />
              </Grid>

              <SectionLabel text="联系信息" />
              <Grid container spacing={2}>
                <FieldRow label="地址" value={data.headquarters} />
                <FieldRow label="网站" value={data.website} />
                <FieldRow label="电话" value={data.phone} />
                <FieldRow label="邮箱" value={data.email} />
                <FieldRow
                  label="官方链接"
                  value={
                    data.same_as?.length ? data.same_as.join("、") : undefined
                  }
                />
              </Grid>

              <SectionLabel text="业务与内容" />
              <Grid container spacing={2}>
                <FieldRow label="公司简介" value={data.company_intro} />
                <FieldRow label="主营业务" value={data.main_business} />
                <FieldRow label="核心优势/差异化" value={data.core_advantage} />
                <FieldRow label="公司规模" value={data.company_size} />
                <FieldRow label="服务区域" value={data.area_served} />
              </Grid>

              <Typography
                sx={{
                  fontSize: 11,
                  color: "text.disabled",
                  mt: 2,
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor: "lineSoft",
                }}
              >
                资产信息由运营方在资产 skill 侧录入维护，网站侧只读展示（PRD 1.2）。
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* 右：账户动态（dashboard recent_activities，公司维度口径见文件头注释） */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                账户动态
              </Typography>
              {dash === null && !dashFailed ? (
                <Loading text="正在加载动态..." />
              ) : dashFailed ? (
                <EmptyState
                  text="账户动态暂未接入"
                  source="动态来自诊断与投放事件（看板接口），接入后自动展示"
                />
              ) : activities.length === 0 ? (
                <EmptyState
                  text="暂无动态"
                  source="动态来自诊断与投放事件 · 产生后自动展示"
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {activities.slice(0, 8).map((activity, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 1.25 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        <Badge
                          label={activity.type}
                          variant={
                            activity.type.includes("诊断")
                              ? "info"
                              : activity.type.includes("确认")
                                ? "success"
                                : activity.type.includes("待确认")
                                  ? "warning"
                                  : "default"
                          }
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                          {activity.title}
                        </Typography>
                        {activity.description && (
                          <Typography
                            sx={{ fontSize: 12, color: "text.secondary" }}
                          >
                            {activity.description}
                          </Typography>
                        )}
                        <Typography
                          sx={{ fontSize: 11, color: "text.disabled", mt: 0.25 }}
                        >
                          {formatRelativeTime(activity.time)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== 名下品牌（概念稿：品牌列表卡，行点击进 /brands/:id） ===== */}
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h4">名下品牌</Typography>
            {brands && <Badge label={`${brands.length} 个`} />}
          </Box>
          {brands === null ? (
            <Loading text="正在加载品牌列表..." />
          ) : (
            <DataTable
              columns={brandColumns}
              data={brands}
              showPagination={false}
              getRowId={(row) => row.id}
              emptyText="名下暂无品牌"
              onRowClick={(row) => navigate(`/brands/${row.id}`)}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
